import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isEmpty } from "lodash-es";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useApi } from "../../shared/api/index";
import { useNotification } from "~hooks/useNotification";

export const useFetchLeadersRules = (params = {}) => {
  const { fetchData } = useApi();
  const { organizationId, sport, leaderCriteriaId } = useParams();
  let URL = `${sport}/o/${organizationId}/statistics/leaders/criteria/${leaderCriteriaId}/qualifiers`;
  if (!isEmpty(params)) {
    URL += `?${new URLSearchParams(params)?.toString()}`;
  }

  return useQuery({
    queryKey: ["leadersRules", organizationId, JSON.stringify(params)],
    queryFn: async () => {
      const result = await fetchData({
        url: URL,
      });
      return result?.data || [];
    },
  });
};

export const useDeleteRule = () => {
  const { mutateData } = useApi();
  const { organizationId, sport, leaderCriteriaId } = useParams();
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: async (qualifierId) => {
      await mutateData({
        url: `${sport}/o/${organizationId}/statistics/leaders/criteria/${leaderCriteriaId}/qualifiers/${qualifierId}`,
        method: "DELETE",
      });
    },
    onSuccess: () => {
      notification.success({
        message: formatMessage({
          id: "rule.delete.success",
          defaultMessage: "Rule deleted successfully",
        }),
        placement: "bottomRight",
      });
      queryClient.invalidateQueries({ queryKey: ["leadersRules"] });
    },
  });
};

export const useCreateRule = () => {
  const { mutateData } = useApi();
  const { organizationId, sport, leaderCriteriaId } = useParams();
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: async (data) => {
      await mutateData({
        url: `${sport}/o/${organizationId}/statistics/leaders/criteria/${leaderCriteriaId}/qualifiers`,
        method: "POST",
        data,
      });
    },
    onSuccess: () => {
      notification.success({
        message: formatMessage({
          id: "leaders.rule.create.success",
          defaultMessage: "Leader Rule created successfully",
        }),
        placement: "bottomRight",
      });
      queryClient.invalidateQueries({ queryKey: ["leadersRules"] });
    },
  });
};

export const useUpdateRule = () => {
  const { mutateData } = useApi();
  const { organizationId, sport, leaderCriteriaId } = useParams();
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: async ({ data, qualifierId }) => {
      await mutateData({
        url: `${sport}/o/${organizationId}/statistics/leaders/criteria/${leaderCriteriaId}/qualifiers/${qualifierId}`,
        method: "PUT",
        data,
      });
    },
    onSuccess: () => {
      notification.success({
        message: formatMessage({
          id: "rule.update.success",
          defaultMessage: "Rule updated successfully",
        }),
        placement: "bottomRight",
      });
      queryClient.invalidateQueries({ queryKey: ["leadersRules"] });
    },
  });
};
